import React from 'react';
import { useSelector } from 'react-redux';
import { getObjectCurriculumElementsDR } from '../../../utils';
import TextNotAssigned from '../../../../../text/TextNotAssigned';
import TextEllipsis from '../../../../../text/TextEllipsis';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextStatusUnitOfLearning from '../../../../../text/TextStatus';

const BasicInformation = ({ isLoading }) => {
  const permissions = useSelector((state) => state?.session?.permissions?.class || {});
  const showStudents = permissions?.list?.includes('all') || permissions?.list?.includes('by-teacher');

  const classData = useSelector((state) => state?.class?.form || {});
  const ulData = useSelector((state) => state?.class?.unitOfLearning || {});

  const subjects = useSelector((state) => state?.subject?.list || []);
  const currentCurriculum = useSelector((state) => state?.assessment?.currentCurriculum || {});
  const years = useSelector((state) => state?.tenant?.years || []);

  const curriculumElements = getObjectCurriculumElementsDR(
    currentCurriculum?.elements,
    currentCurriculum?.elementsStructure,
    classData?.curriculumElements || {}
  );

  const subject = subjects.find((item) => item._id === classData.subjectId);
  const yearLevel = classData.yearLevel?.map((item) => years.find((year) => year._id === item)?.name);

  return (
    <div className='relative w-full h-full'>
    <div className='absolute inset-0 flex'>
      <div className='bg-primary px-4 ' style={{ width: '20%' }}>
        {isLoading ? (
          <div className='flex flex-col justify-center h-full'>
            <Skeleton width={50} height={20} />
            <Skeleton width={120} height={50} />
          </div>
        ) : classData?._id ? (
          <div className='flex flex-col '>
            <TextEllipsis className='text-primary-foreground text-sm opacity-95'>
              {yearLevel?.join(', ') || ''}
            </TextEllipsis>
            <TextEllipsis className='text-primary-foreground font-bold text-4xl'>
              {classData.code}
            </TextEllipsis>
          </div>
        ) : (
          <div className=' flex items-center h-full'>
            <TextNotAssigned color='text-white' size='lg' />
          </div>
        )}
      </div>
      <div
        className='bg-primary px-2 flex flex-col justify-center'
        style={{
          width: '20%',
          backgroundColor: 'rgb(62 119 159 / 75%)'
        }}
      >
        {isLoading ? (
          <>
            <Skeleton width={50} height={20} />
            <Skeleton width={120} height={50} />
          </>
        ) : classData?._id ? (
          <>
            {showStudents && (
              <Typography className='text-primary-foreground font-bold text-sm opacity-90'>
                #{classData?.students?.length} students
              </Typography>
            )}
            <Typography className='text-2xs text-primary-foreground font-bold'>Teachers: </Typography>
            <div className="flex flex-nowrap gap-1 items-center">
              {classData?.teachers?.map((t) => (
                <Tooltip arrow title={`${t.code} - ${t.firstName} ${t.lastName}`}>
                  <div
                  key={`key-teacher-${t._id}`}
                  className="text-xs bg-primary-foreground text-primary px-1  opacity-90 rounded cursor-pointer"
                >
                  {t.lastName}
                </div>
                </Tooltip>
              ))}
            </div>

          </>
        ) : (
          <div className=' flex items-center h-full'>
            <TextNotAssigned color='text-white' size='lg' />
          </div>
        )}
      </div>
      <div
        className=' px-2 text-primary-foreground flex flex-col justify-center'
        style={{
          width: '20%',
          backgroundColor: 'rgb(62 119 159 / 65%)'
        }}
      >
        <Typography className='text-primary font-bold text-xs'>Curriculum</Typography>
        {isLoading ? (
          <Skeleton width={100} height={20} />
        ) : classData?._id ? (
          <>
            <div className='flex gap-1 items-center'>
              <span className='text-sm font-bold text-nowrap'>{curriculumElements?.element?.keyName}</span>
              <TextEllipsis className='text-sm'>
                {curriculumElements?.element?.valueName || <TextNotAssigned />}
              </TextEllipsis>
            </div>
            {curriculumElements?.subElement?.keyName && (
              <div className='flex gap-1 items-center'>
                <span className='text-sm font-bold text-nowrap'>
                  {curriculumElements?.subElement?.keyName}
                </span>
                <TextEllipsis className='text-sm'>
                  {curriculumElements?.subElement?.valueName || <TextNotAssigned />}
                </TextEllipsis>
              </div>
            )}
          </>
        ) : (
          <TextNotAssigned color='text-primary-foreground' />
        )}
      </div>
      <div
        className='px-2 flex flex-col justify-center text-white h-14'
        style={{
          width: '20%',
          backgroundColor: 'rgb(62 119 159 / 50%)'
        }}
      >
        <Typography className='text-primary font-bold text-xs'>Unit of learning</Typography>
        {isLoading ? (
          <Skeleton width={100} height={20} />
        ) : ulData?._id ? (
          <>
            <div className='flex items-center gap-1'>
              <TextStatusUnitOfLearning startDate={ulData.startDate} endDate={ulData.endDate} />
              <TextEllipsis className='font-bold'>{ulData?.name}</TextEllipsis>
            </div>
            {curriculumElements?.element?.keyName && subjects.length > 0 && (
              <div className='flex gap-1 items-center'>
                <span className='text-xs font-bold text-nowrap'>Subject</span>
                <TextEllipsis className='text-xs'>{subject?.name || <TextNotAssigned />}</TextEllipsis>
              </div>
            )}
          </>
        ) : (
          <TextNotAssigned color='text-white' />
        )}
      </div>
      <div
        className='px-2 py-1 flex flex-col justify-start overflow-auto '
        style={{
          backgroundColor: 'white',
          width: '20%'
        }}
      >
        {isLoading ? (
          <>
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={`marking-${index}`} width={100} height={13} />
            ))}
          </>
        ) : classData?._id ? (
          <div>
            {currentCurriculum.marking
              ?.sort((a, b) => a.value - b.value)
              ?.map((m) => (
                <div key={`key-marking-${m._id}`} className='text-xs flex gap-1 items-center rounded'>
                  <FontAwesomeIcon icon='square' style={{ color: m.primaryColor }} />
                  <TextEllipsis style={{ color: m.secondaryColor }} className='text-sm '>
                    {m.name}
                  </TextEllipsis>
                </div>
              ))}
          </div>
        ) : (
          <TextNotAssigned color='text-primary' />
        )}
      </div>
    </div>
    </div>
  );
};

export default BasicInformation;
